import { getBreakpoint } from "./getBreakpoint";
import { BREAKPOINT, BREAKPOINTS, type BreakpointType } from "./constants";

type Listener = (breakpoint: BreakpointType) => void;

class BreakpointObserver {
    private currentBreakpoint: BreakpointType;
    private listeners: Set<Listener> = new Set();
    private mediaQueries: MediaQueryList[] = [];
    private isListening = false;

    constructor() {
        this.currentBreakpoint =
            typeof window === "undefined"
                ? BREAKPOINT.XL
                : getBreakpoint(window.innerWidth);
    }

    private handleMediaChange = (): void => {
        if (typeof window === "undefined") {
            return;
        }

        const newBreakpoint = getBreakpoint(window.innerWidth);

        if (newBreakpoint !== this.currentBreakpoint) {
            this.currentBreakpoint = newBreakpoint;
            this.notify();
        }
    };

    private notify(): void {
        this.listeners.forEach((listener) => {
            listener(this.currentBreakpoint);
        });
    }

    private startListening(): void {
        if (typeof window === "undefined" || this.isListening) {
            return;
        }

        this.mediaQueries = [
            window.matchMedia(`(max-width: ${BREAKPOINTS.S}px)`),
            window.matchMedia(`(max-width: ${BREAKPOINTS.L}px)`),
        ];

        this.mediaQueries.forEach((mq) => {
            mq.addEventListener("change", this.handleMediaChange);
        });

        this.isListening = true;
    }

    private stopListening(): void {
        if (typeof window === "undefined" || !this.isListening) {
            return;
        }

        this.mediaQueries.forEach((mq) => {
            mq.removeEventListener("change", this.handleMediaChange);
        });

        this.mediaQueries = [];
        this.isListening = false;
    }

    public subscribe(listener: Listener): () => void {
        this.listeners.add(listener);

        if (this.listeners.size === 1) {
            this.startListening();
        }

        return () => {
            this.listeners.delete(listener);

            if (this.listeners.size === 0) {
                this.stopListening();
            }
        };
    }

    public getCurrentBreakpoint(): BreakpointType {
        if (typeof window !== "undefined") {
            this.currentBreakpoint = getBreakpoint(window.innerWidth);
        }
        return this.currentBreakpoint;
    }
}

export const breakpointObserver = new BreakpointObserver();
